import { supabase } from "@/lib/supabaseClient";
import { webinarLink, fetchMyReferralCode } from "@/services/webinar";

export type ProspectMessage = {
  id: string;
  direction: "in" | "out" | "note";
  body: string;
  intent: string | null;
  createdAt: string;
};

type MessageRow = {
  id: string;
  direction: "in" | "out" | "note";
  body: string;
  intent: string | null;
  created_at: string;
};

export async function fetchMessages(prospectId: string): Promise<ProspectMessage[]> {
  const { data, error } = await supabase
    .from("prospect_messages")
    .select("id, direction, body, intent, created_at")
    .eq("prospect_id", prospectId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return ((data ?? []) as MessageRow[]).map((r) => ({
    id: r.id,
    direction: r.direction,
    body: r.body,
    intent: r.intent,
    createdAt: r.created_at,
  }));
}

async function currentUser(): Promise<{ id: string; orgId: string | null }> {
  const { data } = await supabase.auth.getUser();
  const id = data.user?.id;
  if (!id) throw new Error("Tu sesión expiró. Vuelve a iniciar sesión.");
  const { data: prof } = await supabase.from("profiles").select("organization_id").eq("id", id).maybeSingle();
  return { id, orgId: (prof?.organization_id as string | null) ?? null };
}

async function insertMessage(
  prospectId: string,
  direction: "in" | "out" | "note",
  body: string,
  channel: string | null,
  intent: string | null,
): Promise<ProspectMessage> {
  const user = await currentUser();
  if (!user.orgId) throw new Error("No se encontró tu organización.");
  const { data, error } = await supabase
    .from("prospect_messages")
    .insert({
      organization_id: user.orgId,
      prospect_id: prospectId,
      author_id: user.id,
      direction,
      body: body.trim().slice(0, 4000),
      channel,
      intent,
    })
    .select("id, direction, body, intent, created_at")
    .single();
  if (error) throw error;
  const r = data as MessageRow;
  return { id: r.id, direction: r.direction, body: r.body, intent: r.intent, createdAt: r.created_at };
}

async function logEvent(prospectId: string, action: string, metadata: Record<string, unknown>, newStage?: string) {
  const { data } = await supabase.auth.getUser();
  await supabase.from("prospect_events").insert({
    prospect_id: prospectId,
    actor_id: data.user?.id ?? null,
    action,
    metadata: newStage ? { ...metadata, stage: newStage } : metadata,
  });
}

export type ProspectPatch = {
  status?: string;
  nextAction: string;
  followUp: string;
  replyCount?: number;
  lastReplyAt?: string;
};

/** Guarda lo que ella escribió, mueve la etapa a "Respondió" (si seguía antes) y deja la próxima acción lista. */
export async function logReply(args: {
  prospectId: string;
  body: string;
  intent: string;
  channel: string | null;
  currentStage: string;
  currentReplyCount: number;
  suggestedStage: string | null;
  nextAction: string;
}): Promise<{ message: ProspectMessage; patch: ProspectPatch }> {
  const message = await insertMessage(args.prospectId, "in", args.body, args.channel, args.intent);

  const early = ["Nuevo", "Contactado"];
  let stage = args.currentStage;
  if (args.intent === "not_interested") {
    stage = args.currentStage; // no se mueve: la persona decide si lo descarta
  } else if (early.includes(args.currentStage)) {
    stage = "Respondió";
  } else if (args.suggestedStage && args.currentStage === "Respondió" && ["Presentación", "Seguimiento"].includes(args.suggestedStage)) {
    stage = args.suggestedStage;
  }

  const followUp = args.intent === "not_interested" ? "" : args.intent === "later" ? "En 3 días" : "HOY";
  const replyCount = args.currentReplyCount + 1;
  const lastReplyAt = message.createdAt;
  const { error } = await supabase
    .from("prospects")
    .update({
      pipeline_stage: stage,
      next_action: args.nextAction,
      follow_up_label: followUp,
      reply_count: replyCount,
      last_reply_at: lastReplyAt,
    })
    .eq("id", args.prospectId);
  if (error) throw error;

  await logEvent(args.prospectId, "reply_received", { intent: args.intent, channel: args.channel }, stage);
  return { message, patch: { status: stage, nextAction: args.nextAction, followUp, replyCount, lastReplyAt } };
}

/** Guarda lo que tú le respondiste (después de enviarlo desde tu cuenta). */
export async function logOutgoing(args: {
  prospectId: string;
  body: string;
  channel: string | null;
  waitingAction?: string;
  /** Días para el próximo seguimiento (null = no insistir). Por defecto 2. */
  followupDays?: number | null;
}): Promise<{ message: ProspectMessage; patch: ProspectPatch }> {
  const message = await insertMessage(args.prospectId, "out", args.body, args.channel, null);
  const nextAction = args.waitingAction ?? "Esperando respuesta";
  const days = args.followupDays === undefined ? 2 : args.followupDays;
  const followUp = days === null ? "" : days === 1 ? "Mañana" : `En ${days} días`;
  const { error } = await supabase
    .from("prospects")
    .update({ next_action: nextAction, follow_up_label: followUp })
    .eq("id", args.prospectId);
  if (error) throw error;
  await logEvent(args.prospectId, "reply_sent", { channel: args.channel });
  return { message, patch: { nextAction, followUp } };
}

let refCache: string | null | undefined;

/** Link único del prospecto al webinar: ?ref=<tu código>&src=<red>&p=<código del prospecto>. */
export async function buildTrackingLink(trackCode: string | undefined, network: string): Promise<string> {
  if (refCache === undefined) {
    try {
      refCache = await fetchMyReferralCode();
    } catch {
      refCache = null;
    }
  }
  const src = network.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 30) || null;
  const base = refCache ? webinarLink(refCache, src) : `${window.location.origin}/webinar${src ? `?src=${encodeURIComponent(src)}` : ""}`;
  if (!trackCode) return base;
  return `${base}${base.includes("?") ? "&" : "?"}p=${encodeURIComponent(trackCode)}`;
}